import { useCallback, useMemo } from 'react';

import { NotificationActions } from 'domains/notifications/store/NotificationActions';
import { ReferralProgram } from 'modules/strapi/types';
import { referralProgramTranslation } from 'modules/referralProgram/translation';
import { removeReferralCodeFromUrl } from 'modules/referralProgram/utils/removeReferralCodeFromUrl';
import { useAppDispatch } from 'store/useAppDispatch';
import { useApplyReferralCode } from 'modules/referralProgram/hooks/useApplyReferralCode';
import { useAuth } from 'modules/auth/hooks/useAuth';
import { useDialog } from 'modules/common/hooks/useDialog';
import { useReferralCode } from 'modules/referralProgram/hooks/useReferralCode';
import { useSavedReferralCode } from 'modules/referralProgram/hooks/useSavedReferralCode';
import { useTranslation } from 'modules/i18n/hooks/useTranslation';

import { IWelcomeDialogProps } from '../WelcomeDialog';

export interface IUseWelcomeDialogProps {
  cover: string;
  handleSignIn: () => void;
  handleSuccessDialogOpen: () => void;
  referralProgram: ReferralProgram | undefined;
}

const { showNotification } = NotificationActions;

export const useWelcomeDialog = ({
  cover,
  handleSignIn,
  handleSuccessDialogOpen,
  referralProgram,
}: IUseWelcomeDialogProps) => {
  const {
    isOpened,
    onClose: handleClose,
    onOpen: handleWelcomeDialogOpen,
  } = useDialog();

  const { referralCode } = useReferralCode();

  const { handleRemoveSavedReferralCode } = useSavedReferralCode();

  const {
    keys: { branded, unbranded },
    t,
  } = useTranslation(referralProgramTranslation);

  const keys = referralProgram ? branded : unbranded;

  const dispatch = useAppDispatch();

  const referralProgramName = referralProgram?.name;

  const onClose = useCallback(() => {
    removeReferralCodeFromUrl();
    handleRemoveSavedReferralCode();

    handleClose();

    dispatch(
      showNotification({
        message: t(keys.activationRejectedErrorMessage),
        severity: 'error',
        title: t(keys.activationRejectedErrorTitle, { referralProgramName }),
      }),
    );
  }, [
    dispatch,
    handleClose,
    handleRemoveSavedReferralCode,
    keys,
    referralProgramName,
    t,
  ]);

  const { isLoggedIn } = useAuth();

  const onSuccess = useCallback(() => {
    handleClose();
    handleSuccessDialogOpen();
  }, [handleClose, handleSuccessDialogOpen]);

  const {
    handleApplyReferralCode: onActivateButtonClick,
    isApplying: isActivating,
  } = useApplyReferralCode({
    hasSuccessNotification: false,
    referralCode,
    onSuccess,
    onError: handleClose,
  });

  const welcomeDialogProps = useMemo(
    (): IWelcomeDialogProps => ({
      cover,
      hasActivateButton: isLoggedIn,
      isActivating,
      onActivateButtonClick,
      onClose,
      onSignInButtonClick: handleSignIn,
      open: isOpened,
      referralProgram,
    }),
    [
      cover,
      handleSignIn,
      isActivating,
      isLoggedIn,
      isOpened,
      onActivateButtonClick,
      onClose,
      referralProgram,
    ],
  );

  return { handleWelcomeDialogOpen, welcomeDialogProps };
};
